import * as React from 'react';
import { Calendar } from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DateRangePickerProps {
  dateRange: { from: Date | undefined; to: Date | undefined };
  onDateRangeChange: (range: { from: Date | undefined; to: Date | undefined }) => void;
  className?: string;
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);

  const presets = [
    { label: 'Today', value: 'today', from: new Date(), to: new Date() },
    {
      label: 'Yesterday',
      value: 'yesterday',
      from: subDays(new Date(), 1),
      to: subDays(new Date(), 1),
    },
    {
      label: 'Last 7 days',
      value: '7days',
      from: subDays(new Date(), 6),
      to: new Date(),
    },
    {
      label: 'Last 30 days',
      value: '30days',
      from: subDays(new Date(), 29),
      to: new Date(),
    },
    {
      label: 'This month',
      value: 'thisMonth',
      from: startOfMonth(new Date()),
      to: endOfMonth(new Date()),
    },
    {
      label: 'Last month',
      value: 'lastMonth',
      from: startOfMonth(subMonths(new Date(), 1)),
      to: endOfMonth(subMonths(new Date(), 1)),
    },
    {
      label: 'Last 3 months',
      value: '3months',
      from: subMonths(new Date(), 3),
      to: new Date(),
    },
    {
      label: 'Last 6 months',
      value: '6months',
      from: subMonths(new Date(), 6),
      to: new Date(),
    },
  ];

  const handlePresetChange = (value: string) => {
    const preset = presets.find((p) => p.value === value);
    if (preset) {
      onDateRangeChange({ from: preset.from, to: preset.to });
    }
  };

  const formatDateRange = () => {
    if (!dateRange.from && !dateRange.to) return 'Select date range';
    if (dateRange.from && !dateRange.to) return `From ${format(dateRange.from, 'MMM d, yyyy')}`;
    if (!dateRange.from && dateRange.to) return `Until ${format(dateRange.to, 'MMM d, yyyy')}`;
    return `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`;
  };

  return (
    <div className={className}>
      <div className="flex gap-2">
        <Select onValueChange={handlePresetChange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Quick select" />
          </SelectTrigger>
          <SelectContent>
            {presets.map((preset) => (
              <SelectItem key={preset.value} value={preset.value}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-[260px] justify-start text-left font-normal"
            >
              <Calendar className="mr-2 h-4 w-4" />
              {formatDateRange()}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="p-3 border-b bg-background-subtle">
              <p className="text-xs text-text-muted">Select start and end dates</p>
            </div>
            <CalendarComponent
              mode="range"
              selected={{ from: dateRange.from, to: dateRange.to }}
              onSelect={(range) =>
                onDateRangeChange({
                  from: range?.from,
                  to: range?.to,
                })
              }
              numberOfMonths={2}
              disabled={(date) => date > new Date()}
              className="rounded-md border-0"
            />
            <div className="p-3 border-t bg-background-subtle flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onDateRangeChange({ from: undefined, to: undefined });
                  setOpen(false);
                }}
              >
                Clear
              </Button>
              <Button
                size="sm"
                onClick={() => setOpen(false)}
              >
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
